import React from 'react';
import JSZip from 'jszip';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import CheckCircleOutline from '@material-ui/icons/CheckCircleOutline';
import CloudUploadOutlined from '@material-ui/icons/CloudUploadOutlined';
import SaveOutlined from '@material-ui/icons/SaveOutlined';
import { BASE_URL, FOOTSKILL_TOOLS_URL } from '../BASE_CONSTS';
import Cookies from '../provider/helpers/Cookies';
import {
  STATISTIC_LIMIT_FIELDS, matchPhotoCandidates,
} from '../custom/settingsModel';

const authHeaders = () => ({ Authorization: `Bearer ${Cookies.getCookie('token')}` });
const fullName = (player = {}) => [player.lastname, player.firstname, player.middlename || player.patronymic].filter(Boolean).join(' ') || `ID ${player.id}`;
const fieldMeta = Object.fromEntries(STATISTIC_LIMIT_FIELDS.map(([key, label, unit]) => [key, { label, unit }]));

async function api(url, options = {}) {
  const response = await fetch(`${String(BASE_URL).replace(/\/+$/, '')}${url}`, {
    ...options, headers: { ...authHeaders(), ...(options.headers || {}) },
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.status === 204 ? null : response.json();
}

const loadPlayers = () => api('/footballers?_limit=-1');
async function toolsApi(path, options = {}) {
  const response = await fetch(`${String(FOOTSKILL_TOOLS_URL).replace(/\/+$/, '')}/${String(path).replace(/^\/+/, '')}`, {
    ...options, headers: { ...authHeaders(), ...(options.headers || {}) },
  });
  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    try { detail = (await response.json()).detail || detail; } catch (_error) { /* keep status */ }
    throw new Error(detail);
  }
  return response.json();
}

function StatisticLimits() {
  const [limits, setLimits] = React.useState({});
  const [violations, setViolations] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [message, setMessage] = React.useState('');

  React.useEffect(() => {
    toolsApi('admin/statistic-limits/').then((data) => setLimits(data.limits || {})).catch((error) => setMessage(`Не удалось загрузить границы: ${error.message}`));
  }, []);
  const saveLimits = async () => {
    setBusy(true); setMessage('');
    try {
      const data = await toolsApi('admin/statistic-limits/', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ limits }) });
      setLimits(data.limits || {}); setMessage('Ограничения сохранены на сервере.');
    } catch (error) { setMessage(`Ошибка сохранения: ${error.message}`); }
    finally { setBusy(false); }
  };
  const check = async () => {
    setBusy(true); setMessage('');
    try {
      const data = await toolsApi('admin/statistic-limits/check/', { method: 'POST' });
      const found = data.issues || [];
      setViolations(found.map((item, index) => ({ ...item, key: `${item.player_id}-${item.statistic_index}-${item.field}-${index}`, editedValue: item.value })));
      setMessage(found.length ? `Найдено превышений: ${found.length}.` : 'Превышений нет.');
    } catch (error) { setMessage(`Ошибка проверки: ${error.message}`); }
    finally { setBusy(false); }
  };
  const saveViolation = async (item) => {
    setBusy(true); setMessage('');
    try {
      await toolsApi('admin/statistic-limits/correct/', { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ player_id: item.player_id, statistic_index: item.statistic_index, field: item.field, value: Number(item.editedValue) }) });
      setViolations((rows) => rows.filter((row) => row.key !== item.key));
      setMessage(`Значение для ${item.player_name || `ID ${item.player_id}`} сохранено.`);
    } catch (error) { setMessage(`Не удалось сохранить: ${error.message}`); }
    finally { setBusy(false); }
  };

  return <section className="fs-settings-card">
    <div className="fs-settings-heading"><div><span>КОНТРОЛЬ</span><h2>Ограничения статистики</h2></div><p>Задайте верхнюю границу. Пустое поле не проверяется.</p></div>
    <div className="fs-limit-grid">{STATISTIC_LIMIT_FIELDS.map(([key, label, unit]) => <TextField key={key} variant="outlined" type="number" label={`${label}, ${unit}`} value={limits[key] ?? ''} onChange={(e) => setLimits((state) => ({ ...state, [key]: e.target.value }))} />)}</div>
    <div className="fs-settings-actions"><Button variant="outlined" onClick={saveLimits} startIcon={<SaveOutlined />}>Сохранить границы</Button><Button className="fs-button-primary" variant="contained" disabled={busy} onClick={check} startIcon={<CheckCircleOutline />}>{busy ? 'Проверка…' : 'Проверить статистику'}</Button></div>
    {message && <div className="fs-settings-notice">{message}</div>}
    {violations.length > 0 && <div className="fs-violation-list"><h3>Значения выше границы</h3>{violations.map((item) => <div className="fs-violation-row" key={item.key}><div><strong>{item.player_name || `ID ${item.player_id}`}</strong><span>{item.phone || 'Телефон не указан'} · {item.date || 'без даты'}</span></div><div className="fs-violation-field"><b>{fieldMeta[item.field].label}</b><small>лимит {item.limit} {fieldMeta[item.field].unit}</small></div><TextField variant="outlined" type="number" error={Number(item.editedValue) > item.limit} value={item.editedValue} onChange={(e) => setViolations((rows) => rows.map((row) => row.key === item.key ? { ...row, editedValue: e.target.value } : row))} /><Button disabled={busy || !Number.isFinite(Number(item.editedValue))} onClick={() => saveViolation(item)}>Сохранить</Button></div>)}</div>}
  </section>;
}

function AvatarArchive() {
  const [rows, setRows] = React.useState([]); const [busy, setBusy] = React.useState(false); const [message, setMessage] = React.useState('');
  const rowsRef = React.useRef([]);
  React.useEffect(() => { rowsRef.current = rows; }, [rows]);
  React.useEffect(() => () => rowsRef.current.forEach((row) => URL.revokeObjectURL(row.preview)), []);
  const inspect = async (event) => {
    const archive = event.target.files && event.target.files[0]; if (!archive) return;
    setBusy(true); setMessage('');
    try {
      const [zip, players] = await Promise.all([JSZip.loadAsync(archive), loadPlayers()]);
      const images = Object.values(zip.files).filter((entry) => !entry.dir && /\.(jpe?g|png|webp)$/i.test(entry.name));
      const prepared = await Promise.all(images.map(async (entry, index) => {
        const candidates = matchPhotoCandidates(entry.name.split('/').pop(), players);
        const blob = await entry.async('blob');
        const file = new File([blob], entry.name.split('/').pop(), { type: blob.type || 'image/jpeg' });
        return { id: `${entry.name}-${index}`, filename: file.name, file, preview: URL.createObjectURL(file), candidates, playerId: candidates.length === 1 ? String(candidates[0].player.id) : '' };
      }));
      setRows((old) => { old.forEach((row) => URL.revokeObjectURL(row.preview)); return prepared; });
      setMessage(`Фотографий в архиве: ${prepared.length}. Проверьте сопоставления.`);
    } catch (error) { setMessage(`Не удалось разобрать ZIP: ${error.message}`); }
    finally { setBusy(false); event.target.value = ''; }
  };
  const apply = async () => {
    const selected = rows.filter((row) => row.playerId); if (!selected.length) return;
    setBusy(true); let done = 0; const errors = [];
    for (const row of selected) {
      try {
        const body = new FormData(); body.append('files', row.file);
        const uploaded = await api('/upload/', { method: 'POST', body });
        await api(`/footballers/${row.playerId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ avatar: uploaded[0] }) });
        done += 1;
      } catch (error) { errors.push(`${row.filename}: ${error.message}`); }
    }
    setBusy(false); setMessage(`Заменено аватаров: ${done}.${errors.length ? ` Ошибок: ${errors.length}. ${errors.join('; ')}` : ''}`);
  };
  return <section className="fs-settings-card"><div className="fs-settings-heading"><div><span>ИМПОРТ</span><h2>Аватары из ZIP-архива</h2></div><p>Поиск по ФИО, фамилии, имени или телефону в имени файла.</p></div><label className="fs-zip-picker"><CloudUploadOutlined /><span>{busy ? 'Обработка…' : 'Выбрать ZIP-архив'}</span><input type="file" accept=".zip,application/zip" disabled={busy} onChange={inspect} /></label>{message && <div className="fs-settings-notice">{message}</div>}{rows.length > 0 && <div className="fs-avatar-list">{rows.map((row) => <div className="fs-avatar-match" key={row.id}><img src={row.preview} alt="" /><div><strong>{row.filename}</strong><span>{row.candidates.length ? `Найдено: ${row.candidates.length}` : 'Совпадений нет'}</span></div><TextField select variant="outlined" label="Кому назначить" value={row.playerId} onChange={(e) => setRows((items) => items.map((item) => item.id === row.id ? { ...item, playerId: e.target.value } : item))}><MenuItem value="">Не заменять</MenuItem>{row.candidates.map(({ player }) => <MenuItem key={player.id} value={String(player.id)}>{fullName(player)} · {player.f_email || 'без телефона'}</MenuItem>)}</TextField></div>)}<div className="fs-settings-actions"><Button className="fs-button-primary" variant="contained" disabled={busy || !rows.some((row) => row.playerId)} onClick={apply}>{busy ? 'Загрузка…' : 'Подтвердить и заменить'}</Button></div></div>}</section>;
}

export default function SettingsPage() { return <main className="fs-settings-page"><div className="fs-settings-title"><span>АДМИНИСТРИРОВАНИЕ</span><h1>Настройки</h1><p>Проверка данных и пакетные операции.</p></div><StatisticLimits /><AvatarArchive /></main>; }
